import { Json } from "@/integrations/supabase/types";
import type { HubConfig } from "./info-links";
import type { ListenAlertSettings } from "./listen-alerts";
import { AgentMailbox } from "@/types/agent-mailboxes";
import { GeniePurpose } from "@/types/genie-purpose";
import { DeferredDocumentAction } from "@/types/knowledge/deferred-operations";
import { KnowledgeBase } from "@/types/knowledge/knowledge-base";
import { LeadInfoPreset } from "@/types/leads/lead-info";
import { SavedQRCode } from "@/types/qr-code/saved-qr-code";
import { GenieBranding } from "@/types/ui/branding";
import type { CustomerMemoryConsentStatus } from "@/types/voice/customer-memory";

/** A notification channel HG Sync has connected for a genie. */
export interface ConnectedChannel {
  id: string;
  type: string;
  name: string;
  status: "connected" | "attention";
  last_sync_at: string | null;
  /** Where notifications for this channel actually go — e.g. a Slack channel name, an email address, a phone number. */
  receiver: string | null;
}

export interface ElevenLabsAgent {
  agent_id: string;
  name: string;
  metadata: {
    created_at_unix_secs: number;
  };
}

export interface AgentConfig {
  name?: string;
  conversation_config?: {
    agent: {
      first_message?: string;
      language?: string;
      dynamic_variables?: {
        dynamic_variable_placeholders: Record<string, string>;
      };
      prompt?: {
        prompt?: string;
        llm?: string;
        temperature?: number;
        max_tokens?: number;
        knowledge_base?: Array<KnowledgeBase>;
        built_in_tools?: {
          transfer_to_number?: {
            name: string;
            description?: string;
            params: {
              system_tool_type: "transfer_to_number";
              enable_client_message?: boolean;
              transfers: Array<{
                condition: string;
                transfer_destination?:
                  | {
                      phone_number: string;
                      type?: "phone";
                    }
                  | {
                      sip_uri: string;
                      type: "sip";
                    }
                  | null;
                transfer_type?: "conference" | "blind" | "sip_refer";
              }>;
            };
          } | null;
        };
        tool_ids?: Array<string>;
        rag?: {
          enabled?: boolean;
        };
        backup_llm_config?:
          | { preference: "default" }
          | { preference: "disabled" }
          | { preference: "override"; order: Array<string> };
      };
    };
    tts?: {
      voice_id?: string;
      stability?: number;
      similarity_boost?: number;
      speed?: number;
      model_id?: string;
    };
    conversation?: {
      text_only?: boolean;
      max_duration_seconds?: number;
      client_events?: string[];
    };
    turn?: {
      turn_timeout?: number;
      silence_end_call_timeout?: number;
      mode?: "silence" | "turn";
    };
  };
  platform_settings?: {
    call_limits?: {
      daily_limit?: number;
    };
    overrides?: {
      enable_conversation_initiation_client_data_from_webhook?: boolean;
    };
  };
  support_team_emails?: string[];
  pronunciations?: PronunciationPair[];
  pronunciation_dictionary_id?: string;
  pronunciation_dictionary_version_id?: string;
  sync_tools?: SyncTool[];
  media_sources?: MediaSourcesConfig;
  goal_collection_effort?: GoalCollectionEffort;
}

/** How hard a genie tries to collect its insight details during a conversation. */
export type GoalCollectionEffort = "listen_only" | "balanced" | "thorough";

/** Where "Show Links & Images" may look once the genie's own content has no match. */
export type MediaSourceMode = "web" | "approved_sites" | "own_content";

export interface MediaSourcesConfig {
  mode: MediaSourceMode;
  /** Bare domains (no scheme, no www); only applied in "approved_sites" mode. */
  allowed_domains: string[];
}

export interface PronunciationPair {
  word: string;
  pronunciation: string;
}

export interface SyncTool {
  id: string;
  name: string;
  description: string;
  trigger_type: "during_call" | "after_call";
}

export interface AgentResponse {
  agent_id: string;
  name: string;
  conversation_config: {
    asr?: Record<string, any>;
    turn?: Record<string, any>;
    tts?: Record<string, any>;
    conversation?: Record<string, any>;
    agent?: Record<string, any>;
    [key: string]: any;
  };
  metadata: {
    created_at_unix_secs: number;
    [key: string]: any;
  };
}

export interface CreateAgentParams {
  name: string;
  description?: string;
  brand?: string;
  is_active?: boolean;
  settings?: Json | AgentConfig;
  elevenlabs_id?: string;
  user_id?: string;
  redirect_url?: string | null;
}

export interface AgentCreationParams {
  name: string;
  description?: string;
  useCase?: string;
  language?: string;
  firstMessage?: string;
  systemPrompt?: string;
  voiceId?: string;
  llmModel?: string;
  temperature?: number;
  maxTokens?: number;
  stability?: number;
  similarity?: number;
  speed?: number;
  textOnly?: boolean;
  maxDurationSeconds?: number;
  turnTimeout?: number;
  silenceTimeout?: number;
  turnMode?: "silence" | "turn";
  knowledgeBases?: Array<{ id: string; name: string; type: string }>;
  supportEmails?: string[];
  pronunciations?: Array<{ word: string; pronunciation: string }>;
  brand?: string;
  category?: string;
  isPublic?: boolean;
  dynamicVariables?: Record<string, string>;
  sendSandboxLink?: boolean;
  sendDemoLink?: boolean;
  sendPricingData?: boolean;
  attachPhoneNumber?: boolean;
  isoCountryCode?: string;
  sendConversationReports?: boolean;
  branding?: GenieBranding;
  redirectUrl?: string | null;
  goal?: string;
  deferredDocumentActions?: DeferredDocumentAction[];
}

export interface VoiceSearchResult {
  voice_id: string;
  name: string;
  category: string;
  description?: string;
}

export interface VoiceSearchResponse {
  voices: VoiceSearchResult[];
  searchTerms: string[];
}

export interface AgentToken {
  agent_id: string;
  conversation_token: string;
  expiration_time_unix_secs: number;
  purpose: "signed_url";
}

export interface AgentLinkResponse {
  agent_id: string;
  token?: AgentToken;
}

export const CLIENT_EVENTS = [
  { value: "audio", label: "Audio", description: "Enable the genie to speak with voice output" },
  {
    value: "interruption",
    label: "Interruption",
    description: "Allow users to interrupt and talk while the genie is speaking",
  },
  {
    value: "user_transcript",
    label: "User Transcript",
    description: "Display live text transcripts of what users say during the conversation",
  },
  {
    value: "agent_response",
    label: "Agent Response",
    description: "Display live text transcripts of what the genie says during the conversation",
  },
  {
    value: "client_tool_call",
    label: "Client Tool Call",
    description: "Enable the genie to use client-side tools during conversations",
  },
  {
    value: "agent_tool_request",
    label: "Agent Tool Request",
    description: "Tell the page when the genie starts a tool, so a website chat can connect visitors to a person",
  },
  {
    value: "mcp_tool_call",
    label: "MCP Tool Call",
    description: "Enable the genie to use MCP (Model Context Protocol) tools during conversations",
  },
  {
    value: "mcp_connection_status",
    label: "MCP Connection Status",
    description: "Show whether MCP connection is active and available for the genie to use",
  },
  {
    value: "agent_tool_response",
    label: "Agent Tool Response",
    description: "Allow the genie to use responses from client tools in the conversation",
  },
] as const;

export const DEFAULT_CLIENT_EVENTS = ["audio", "interruption"] as const;

export interface AgentPhone {
  id: string;
  agent_id: string | null;
  el_agent_id: string | null;
  number: string | null;
  friendly_name: string | null;
  elevenlabs_id: string | null;
  twilio_sid: string | null;
  status: string | null;
  country_code: string | null;
  metadata: any | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AgentPageMetadata {
  redirect_url: string | null;
}

export interface AgentPage {
  id: string | null;
  agent_id: string | null;
  el_agent_id: string | null;
  url_name: string | null;
  welcome_message: string | null;
  target_audience: string | null;
  genie_image_url?: string | null;
  branding?: any | null;
  metadata: AgentPageMetadata | null;
  sandbox_link: string | null;
  info_links?: any | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AgentMinimal {
  id: string;
  name: string;
  elevenlabs_id: string | null;
  is_active: boolean;
  category: string | null;
  position: number;
  created_at: string | null;
}

export interface Agent {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  brand: string | null;
  elevenlabs_id: string;
  is_active: boolean;
  is_setup: boolean;
  deactivated_manually: boolean;
  category: string | null;
  category_data: any | null;
  voice: string | null;
  voice_label: string | null;
  send_conv_reports: boolean;
  report_mode: "every_conversation" | "digest" | "escalation_only" | "off" | "auto";
  report_digest_frequency: "weekly" | "fortnightly" | "monthly" | null;
  report_digest_day_of_week: number | null;
  report_digest_last_sent_at: string | null;
  escalation_settings: {
    channels?: string[];
    recipients?: { emails?: string[] };
    min_severity?: "low" | "medium" | "high";
  } | null;
  /** Active email recipients for conversation reports/digests, from notification_recipients. */
  report_recipients?: string[];
  /** Active email recipients for escalation alerts, from notification_recipients. */
  escalation_recipients?: string[];
  /** Report recipients blocked due to a Postmark bounce (excluded from report_recipients). */
  blocked_report_recipients?: string[];
  /** Escalation recipients blocked due to a Postmark bounce (excluded from escalation_recipients). */
  blocked_escalation_recipients?: string[];
  /** Notification channels HG Sync has connected for this genie, resolved server-side per request — not a persisted column. */
  connected_channels?: ConnectedChannel[];
  auto_report_decision_prompt: string | null;
  auto_report_decision_prompt_generated_at: string | null;
  /** Listener genies only: urgent alerts, daily brief, nudge and SMS settings. Null means the defaults. */
  listen_alerts?: Partial<ListenAlertSettings> | null;
  redirect_url: string | null;
  capture_leads: boolean;
  memory_enabled: boolean;
  /** Formatted "what this genie remembers about you" block for the current
   * viewer, resolved server-side per request — not a persisted column. */
  userMemory?: string | null;
  voice_bridge_enabled: boolean;
  voice_bridge_show_button: boolean;
  /** Bound to a Help Genie Outbound workspace, so visitors can request a callback. */
  outbound_connected?: boolean;
  /** When the Genie was connected to Outbound — owner-facing payloads only. */
  outbound_connected_at?: string | null;
  userMemory?: string | null;
  memoryConsent?: CustomerMemoryConsentStatus;
  settings: AgentConfig | null;
  branding: any | null;
  position: number | null;
  group_id: number | null;
  call_limit: number | null;
  created_at: string | null;
  updated_at: string | null;
  goal: LeadInfoPreset | null;
  insights: LeadInfoPreset | null;
  phone: AgentPhone | null;
  transfer_phone?: AgentPhone | null;
  transfer_phones?: AgentPhone[];
  escalation_phone?: AgentPhone | null;
  escalation_phones?: AgentPhone[];
  page: AgentPage;
  escalation_webhook: { is_active: boolean } | null;
  mailbox: AgentMailbox | null;
  is_marketplace_genie: boolean;
  is_demo_genie: boolean;
  is_public: boolean;
  persona_id: string | null;
  saved_qr_codes: SavedQRCode[];
  type: string | null;
  url_name: string | null;
  welcome_message: string | null;
  info_links: any | null;
  /** Genie Page (`/help/:url_name`) settings. Null until the owner first saves. */
  hub_config: HubConfig | null;
  /** First-content approval stamp. Only an explicit null keeps the page private. */
  hub_approved_at: string | null;
  purpose: GeniePurpose;
  owner?: { id: string; full_name: string | null; email: string | null; role?: string } | null;
  [key: string]: any;
}

export const DefaultConfig = {
  language: "en",
  voiceId: "cjVigY5qzO86Huf0OWal",
  llmModel: "gpt-5.6-luna",
  temperature: 0.7,
  maxTokens: 8000,
  stability: 0.5,
  similarity: 0.8,
  speed: 1,
  textOnly: false,
  maxDurationSeconds: 60 * 30, // 30 minutes,
  turnTimeout: 7,
  silenceTimeout: 60 * 5, // 5 minutes
  turnMode: "silence" as const,
  isPublic: false,
} as const;
